import { useGetBreeds } from '@application/api';
import {
  filterNotEmpty,
  getRandomIntInclusive,
  useConfirmModal,
  useRouter,
} from '@application/utils';
import { AnimalTypeEnum, ProfessionalTypeEnum } from '@gql/graphql';
import type {
  BookingSlotPickerData,
  BookingSlotPickerFormValues,
  BookingSlotPickerProps,
  PreBookingWarningData,
  SelectOption,
} from '@presentation/components';
import { BookingSlotPicker, PrebookingWarningModal } from '@presentation/components';
import { BREAKPOINTS } from '@presentation/styles';
import dayjs from 'dayjs';
import type { FormikConfig } from 'formik';
import { FormikProvider, useFormik } from 'formik';
import { sortBy } from 'lodash/fp';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useBookingAvailabilities } from './api/useBookingAvailabilities';
import { useGetPro } from './api/useGetPro';
import type { BookingSlotPickerControllerProps } from './types';

export const BookingSlotPickerController: FC<BookingSlotPickerControllerProps> = (props) => {
  const {
    proUrl,
    prefillValues,
    updateMode = false,
    disabled = false,
    onAvailSelect,
    customFormTitle,
    customSubmit,
  } = props;

  const initialValues = useMemo((): BookingSlotPickerFormValues => {
    return {
      forMultiAnimals: prefillValues?.forMultiAnimals ?? false,
      serviceId: prefillValues?.proServiceId ?? undefined,
      workerId: prefillValues?.workerId ?? undefined,
      animalType: prefillValues?.animalType ?? undefined,
      breedId: prefillValues?.breedId ?? undefined,
      bookingStart: undefined,
    };
  }, [
    prefillValues?.animalType,
    prefillValues?.breedId,
    prefillValues?.forMultiAnimals,
    prefillValues?.proServiceId,
    prefillValues?.workerId,
  ]);

  const submitHandler: FormikConfig<BookingSlotPickerFormValues>['onSubmit'] = async (values) => {
    let confirmed = !hasWarning;
    if (!confirmed) {
      confirmed = await confirmedShow?.();
    }
    if (!confirmed) return;

    const workerId = workerService?.workerId;
    const workerServiceId = workerService?.id;

    if (!workerServiceId || !workerId) {
      throw new Error(`workerService (${workerServiceId}) or workedId (${workerId}) is required`);
    }

    if (customSubmit) {
      customSubmit({ ...values, workerId });
    } else {
      navigateToRoute('booking', {
        search: {
          proUrl,
          workerId,
          workerServiceId,
          bookingStart: values.bookingStart,
          animalType: values.animalType,
          breedId: values.breedId,
          forMultiAnimals: values.forMultiAnimals,
        },
      });
    }
  };

  const form = useFormik<BookingSlotPickerFormValues>({
    initialValues,
    onSubmit: submitHandler,
    validateOnChange: true,
    enableReinitialize: true,
  });

  const { navigateToRoute, translate } = useRouter();

  const [fromDate, setFromDate] = useState(dayjs());

  const { modal, confirmedShow, confirmHandler } = useConfirmModal();

  const { pro, proLoading } = useGetPro({ url: proUrl, id: prefillValues?.proId });

  const { breedOptionsWithCrossbreedsFirst, breeds, breedsLoading } = useGetBreeds({
    skip: !pro || pro.type !== ProfessionalTypeEnum.GROOMER,
  });

  const workerServiceByWorkerId = useMemo(() => {
    const { workerId, serviceId } = form.values;
    if (!workerId || !serviceId) return undefined;
    // eslint-disable-next-line no-restricted-syntax
    for (const service of pro?.services ?? []) {
      // eslint-disable-next-line no-restricted-syntax
      for (const workerService of service?.workerServices ?? []) {
        if (service?.id === serviceId && workerService.workerId === workerId) {
          return workerService;
        }
      }
    }
  }, [form.values, pro?.services]);

  const { availabilities, nextAvailability, availabilitiesLoading } = useBookingAvailabilities({
    proId: pro?.id,
    serviceId: form.values.serviceId,
    workerServiceId: workerServiceByWorkerId?.id,
    fromDate: fromDate.startOf('day'),
    anyWorker: form.values.workerId === 'any',
    forMultiAnimals: form.values.forMultiAnimals,
  });

  useEffect(() => {
    setFromDate(dayjs());
    form.setFieldValue('bookingStart', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.workerId, form.values.serviceId, form.values.animalType, form.values.breedId]);

  const workerServiceByAny = useMemo(() => {
    const { workerId } = form.values;
    if (!workerId) return undefined;
    if (workerId === 'any' && form.values.bookingStart) {
      // Find workerService by taking the first worker of availabities
      const availability = (availabilities ?? []).find(
        (x) => +dayjs(x?.startDateTime) === +dayjs(form.values.bookingStart),
      );
      const workers = availability?.workerIds;
      if (!workers?.length) return undefined;
      const randomWorker = workers[getRandomIntInclusive(0, workers.length - 1)];
      const randomWorkerService = (pro?.services || [])
        .find((proService) => proService?.id === form.values.serviceId)
        ?.workerServices.find((ws) => ws.workerId === randomWorker);
      return randomWorkerService;
    }
  }, [availabilities, form.values, pro?.services]);

  const workerService = useMemo(() => {
    return workerServiceByWorkerId ?? workerServiceByAny;
  }, [workerServiceByAny, workerServiceByWorkerId]);

  const warning = useMemo((): PreBookingWarningData => {
    return {
      proWarning: pro?.translations?.[0]?.preBookingWarning ?? undefined,
      workerServiceWarning: workerService?.translations?.[0]?.preBookingWarning ?? undefined,
    };
  }, [pro?.translations, workerService?.translations]);

  const hasWarning = useMemo(
    () => warning.proWarning || warning.workerServiceWarning,
    [warning.proWarning, warning.workerServiceWarning],
  );

  const getWorkerOptions = useCallback(
    (serviceId?: string, forMultiAnimals?: boolean): BookingSlotPickerData['workerOptions'] => {
      const options = (pro?.workers ?? [])
        .filter((worker) => {
          const hasValidProService = (pro?.services ?? []).some(
            (proService) =>
              proService?.id === serviceId &&
              proService?.workerServices.some((ws) => {
                if ((forMultiAnimals ?? false) && !ws.multiAnimalsBookingEnabled) {
                  return false;
                }
                return ws.bookable && ws.workerId === worker.id;
              }),
          );
          return hasValidProService;
        })
        .map(
          (worker) =>
            ({
              label: worker.fullName,
              value: worker.id,
            } as SelectOption<number | 'any'>),
        );

      if (pro?.allowsAnyWorkerOption && options.length > 1) {
        options.unshift({
          label: translate('glossary.firstAvailable'),
          value: 'any',
        });
      }
      return options;
    },
    [pro?.allowsAnyWorkerOption, pro?.services, pro?.workers, translate],
  );

  const smallBreakpoint = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.small}px)` });
  const [daysDisplayedCount, setDaysDisplayedCount] = useState(3);

  useEffect(() => {
    if (smallBreakpoint === false) {
      setDaysDisplayedCount(4);
    }
  }, [smallBreakpoint]);

  const browseBookingDates: BookingSlotPickerData['browseBookingDates'] = useCallback(
    (direction) => {
      switch (direction) {
        case 'future':
          setFromDate(fromDate.add(daysDisplayedCount, 'days'));
          break;
        case 'nextAvailability':
          setFromDate(dayjs(nextAvailability?.date));
          break;
        default:
        case 'past':
          setFromDate(dayjs.max(dayjs(), fromDate.subtract(daysDisplayedCount, 'days')));
          break;
      }
    },
    [fromDate, nextAvailability, daysDisplayedCount],
  );

  const daysWithAvails = useMemo(
    (): BookingSlotPickerProps['daysWithAvails'] =>
      availabilities.length
        ? new Array(daysDisplayedCount).fill(undefined).map((_, i) => {
            const date = fromDate.add(i, 'days');
            const avails = (availabilities || [])
              .filter(filterNotEmpty)
              .filter((x) => dayjs(x?.startDateTime).isSame(date, 'date'))
              .filter((x) => Boolean(x));
            return { date, avails };
          })
        : [],
    [availabilities, daysDisplayedCount, fromDate],
  );

  const isGroomer = useMemo(() => {
    return pro?.type === ProfessionalTypeEnum.GROOMER;
  }, [pro?.type]);

  const getServiceOptions = useCallback(
    (values: BookingSlotPickerFormValues): BookingSlotPickerData['serviceOptions'] => {
      const filteredServices = (pro?.services ?? []).filter((proService) => {
        const isDog = values.animalType === AnimalTypeEnum.DOG;

        const dogBreed = breeds.find((b) => b.id === values.breedId);
        const dogBreedSize = dogBreed?.sizeCategory ?? undefined;

        if (!proService?.bookable) return false;
        if (!isGroomer) {
          const workersServicesFilteredByMultipleAnimals = proService.workerServices.filter(
            (x) => !values.forMultiAnimals || x.multiAnimalsBookingEnabled,
          );

          return workersServicesFilteredByMultipleAnimals.length > 0;
        }
        if (!values.animalType) return false;

        const includesAnimalType = proService.workerServices
          .map((x) => x.animalType)
          .includes(values.animalType);

        if (!isDog) {
          return includesAnimalType;
        }

        if (!dogBreedSize) return false;

        const includesSize = proService.workerServices
          .map((x) => x.animalSize)
          .includes(dogBreedSize);

        return includesAnimalType && includesSize;
      });

      const sortedService = sortBy(
        (x) => x?.service?.translations?.[0]?.title?.toLocaleLowerCase(),
        filteredServices,
      );

      return sortedService
        .filter((service) => !!service && !!service.id)
        .map((proService) => ({
          label: proService!.service.translations[0]?.title,
          value: proService!.id,
        }));
    },
    [breeds, isGroomer, pro?.services],
  );

  const hasServicesForMultiAnimal = useMemo(() => {
    return getServiceOptions({ ...form.values, forMultiAnimals: true }).length > 0;
  }, [form.values, getServiceOptions]);

  const serviceOptions: BookingSlotPickerData['serviceOptions'] = useMemo(() => {
    return getServiceOptions(form.values);
  }, [form.values, getServiceOptions]);

  const workerOptions: BookingSlotPickerData['workerOptions'] = useMemo(() => {
    return getWorkerOptions(form.values.serviceId, form.values.forMultiAnimals);
  }, [form.values, getWorkerOptions]);

  const animalOptions = useMemo((): BookingSlotPickerData['animalOptions'] => {
    return Object.keys(AnimalTypeEnum)
      .map((key) => {
        return {
          value: key as AnimalTypeEnum,
          label: translate(`common.options.animals.${key.toLowerCase()}`),
        };
      })
      .filter((option) => pro?.acceptedAnimals.includes(option.value as AnimalTypeEnum));
  }, [pro?.acceptedAnimals, translate]);

  const shownFields: BookingSlotPickerData['shownFields'] = useMemo(() => {
    const fields: Array<keyof BookingSlotPickerFormValues> = [];
    if (isGroomer) {
      fields.push('animalType');
    }
    const bookingIsForDog = form.values.animalType === AnimalTypeEnum.DOG;
    if (isGroomer && bookingIsForDog) {
      fields.push('breedId');
    }
    if (!isGroomer && hasServicesForMultiAnimal) {
      fields.push('forMultiAnimals');
    }
    fields.push('serviceId');
    fields.push('workerId');
    fields.push('bookingStart');
    return fields;
  }, [form.values.animalType, hasServicesForMultiAnimal, isGroomer]);

  useEffect(() => {
    if (form.values.serviceId && !form.values.workerId && workerOptions.length === 1) {
      form.setFieldValue('workerId', workerOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerOptions, form.values.workerId, form.values.serviceId]);

  const bookingSlotWarning = useMemo((): string | undefined => {
    if (!updateMode) return;
    if (!pro?.minHoursBeforeUpdateOrCancelBooking) return;
    const timeFormatted = pro.minHoursBeforeUpdateOrCancelBooking.toString();
    return translate('mt.buyer.booking.warning.before').replace('$time', timeFormatted);
  }, [pro?.minHoursBeforeUpdateOrCancelBooking, translate, updateMode]);

  return (
    <FormikProvider value={form}>
      <BookingSlotPicker
        loading={proLoading || breedsLoading}
        warning={bookingSlotWarning}
        fromDate={fromDate}
        updateMode={updateMode}
        proType={pro?.type}
        customFormTitle={customFormTitle}
        daysWithAvails={daysWithAvails}
        onAvailSelect={onAvailSelect}
        browseBookingDates={browseBookingDates}
        daysDisplayedCount={daysDisplayedCount}
        breedOptions={breedOptionsWithCrossbreedsFirst}
        serviceOptions={serviceOptions}
        workerOptions={workerOptions}
        animalOptions={animalOptions}
        shownFields={shownFields}
        availabilitiesLoading={availabilitiesLoading}
        nextAvailability={nextAvailability ?? undefined}
        disabled={disabled}
      />
      <PrebookingWarningModal
        modal={modal}
        onConfirm={() => {
          confirmHandler();
        }}
        {...warning}
      />
    </FormikProvider>
  );
};
